import React, { useState, useEffect } from "react";
import { Drawer, Dropdown, DatePicker, TimePicker } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import moment from "moment";
import { Button } from "components/Button";
import { postAddClassV2 } from "actions/classRoom";
import useCourseEndDate from "../hooks/useCourseEndDate";
import FloatingLabelInput from "../utils/floatingLabelInput";
import "../styles/addBatchDrawer.css";

const defaultForm = {
    batchName: "",
    sectionName: "",
    startDate: null,
    endDate: null,
    startTime: null,
    endTime: null,
    repeat: "",
    strength: "",
    stopAdmission: false,
    selectedDays: new Set()
};

const daysList = [
    { label: "S", value: '1' },
    { label: "M", value: '2' },
    { label: "T", value: '3' },
    { label: "W", value: '4' },
    { label: "T", value: '5' },
    { label: "F", value: '6' },
    { label: "S", value: '7' },
];

const repeatOptions = ["Mon-Fri", "Mon-Sat", "Mon-Sun", "Sat-Sun", "Custom"];

const AddBatchDrawer = ({ visible, onClose, onSuccess, courseName, courseId, courseType }) => {
    const dispatch = useDispatch();
    const [form, setForm] = useState(defaultForm);
    const [submitted, setSubmitted] = useState(false);
    const [repeatDropdownOpen, setRepeatDropdownOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // Autofills the end date from the course duration.
    const getAutoEndDate = useCourseEndDate(visible, courseId);

    useEffect(() => {
        if (visible) {
            setForm(defaultForm);
            setSubmitted(false);
            setSubmitting(false);
        }
    }, [visible]);

    const handleStartDateChange = (date) => {
        const autoEnd = getAutoEndDate(date);
        setForm(prev => ({
            ...prev,
            startDate: date,
            // Cleared start date clears the end date too.
            ...(!date ? { endDate: null } : autoEnd ? { endDate: autoEnd } : {}),
        }));
    };

    const updateField = (key, value) => {
        setForm(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const toggleDay = (dayValue) => {
        const newSelected = new Set(form.selectedDays);
        if (newSelected.has(dayValue)) {
            newSelected.delete(dayValue);
        } else {
            newSelected.add(dayValue);
        }
        updateField("selectedDays", newSelected);
    };

    const handleClose = () => {
        setForm(defaultForm);
        setSubmitted(false);
        setSubmitting(false);
        onClose();
    };

    const handleAdd = () => {
        setSubmitted(true);
        if (isCreateDisabled || submitting) return;

        const dayMap = { Sun: "1", Mon: "2", Tue: "3", Wed: "4", Thu: "5", Fri: "6", Sat: "7" };
        let days = [];
        if (form.repeat === 'Custom') {
            days = Array.from(form.selectedDays).map(String);
        } else if (form.repeat && form.repeat.includes("-")) {
            const dayOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
            const [start, end] = form.repeat.split("-");
            if (dayMap[start] && dayMap[end]) {
                const startIndex = dayOrder.indexOf(start);
                const endIndex = dayOrder.indexOf(end);
                days = dayOrder.slice(startIndex, endIndex + 1).map(day => dayMap[day]);
            }
        }

        const parsedCourseId = (courseId !== undefined && courseId !== null && courseId !== "") ? (typeof courseId === "number" ? courseId : parseInt(courseId, 10) || courseId) : "";

        const sectionObj = {
            id: "",
            index: 1,
            startDate: form.startDate ? moment(form.startDate).format("YYYY-MM-DD") : "",
            endDate: form.endDate ? moment(form.endDate).format("YYYY-MM-DD") : "",
            stopAdmission: Boolean(form.stopAdmission),
            isExpanded: true,
            courseId: parsedCourseId,
            courseType: courseType || "course",
            curriculum: false,
            studentsCount: 0,
            alreadyImported: null,
            section: form.sectionName,
            maxStrength: form.stopAdmission && form.strength ? parseInt(form.strength, 10) : 0,
            startTime: form.startTime ? moment(form.startTime).format("HH:mm:ss") : "",
            endTime: form.endTime ? moment(form.endTime).format("HH:mm:ss") : "",
            days: days,
        };

        const params = {
            std: form.batchName,
            oldStd: form.batchName,
            isNew: true,
            standards: [sectionObj]
        };

        setSubmitting(true);
        dispatch(postAddClassV2(
            params,
            () => setSubmitting(false),
            (response) => {
                setSubmitting(false);
                if (response && response?.show?.type !== "error") {
                    if (onSuccess) {
                        onSuccess(response);
                    }
                    handleClose();
                }
            }
        ));
    };

    const isEndTimeInvalid = Boolean(
        form.startTime &&
        form.endTime &&
        moment(form.endTime).format("HH:mm:ss") <= moment(form.startTime).format("HH:mm:ss")
    );

    const disabledEndTimeHours = () => {
        if (!form.startTime) return [];
        const start = moment(form.startTime);
        if (!start.isValid()) return [];
        const startHour = start.hour();
        const startMinute = start.minute();
        const hours = [];
        for (let i = 0; i < startHour; i++) {
            hours.push(i);
        }
        if (startMinute === 59) {
            hours.push(startHour);
        }
        return hours;
    };

    const disabledEndTimeMinutes = (selectedHour) => {
        if (!form.startTime) return [];
        const start = moment(form.startTime);
        if (!start.isValid()) return [];
        const startHour = start.hour();
        const startMinute = start.minute();
        const targetHour =
            selectedHour !== undefined && selectedHour >= 0
                ? selectedHour
                : (form.endTime && moment(form.endTime).isValid() ? moment(form.endTime).hour() : startHour);

        const minutes = [];
        if (targetHour === startHour) {
            for (let i = 0; i <= startMinute; i++) {
                minutes.push(i);
            }
        } else if (targetHour < startHour) {
            for (let i = 0; i < 60; i++) {
                minutes.push(i);
            }
        }
        return minutes;
    };

    const getDefaultEndTime = () => {
        if (!form.startTime) return undefined;
        const start = moment(form.startTime);
        if (!start.isValid()) return undefined;
        if (start.hour() < 23) {
            return moment(start).add(1, "hour");
        }
        if (start.minute() < 55) {
            return moment(start).add(5, "minutes");
        }
        return moment(start).minute(59);
    };

    const isCreateDisabled =
        !form.batchName ||
        !form.sectionName ||
        !form.startDate ||
        !form.startTime ||
        !form.endTime ||
        isEndTimeInvalid ||
        !form.repeat ||
        (form.repeat === "Custom" && form.selectedDays.size === 0) ||
        (form.stopAdmission && !form.strength) ||
        submitting;

    return (
        <Drawer
            visible={visible}
            placement="right"
            width="32%"
            destroyOnClose
            closable={false}
            className="add-batch-drawer"
            onClose={handleClose}
            bodyStyle={{
                padding: 0,
                display: "flex",
                flexDirection: "column",
                height: "100%",
            }}
        >
            {/* Header */}
            <div className="add-batch-header">
                <div className="add-batch-title">Add a new batch</div>
                <CloseOutlined className="add-batch-close" onClick={handleClose} />
            </div>

            {/* Body */}
            <div className="add-batch-body">

                {/* Course Field - Disabled */}
                <div className="addBatch-field">
                    <FloatingLabelInput
                        label="Course"
                        required
                        value={courseName || ""}
                        disabled
                    />
                </div>

                {/* Batch Name */}
                <div className="addBatch-field">
                    <FloatingLabelInput
                        label="Batch name"
                        required
                        value={form.batchName}
                        onChange={(e) => updateField("batchName", e.target.value)}
                        error={submitted && !form.batchName}
                        errorMessage="Batch name is required"
                    />
                </div>

                {/* Section Name */}
                <div className="addBatch-field">
                    <FloatingLabelInput
                        label="Section"
                        required
                        value={form.sectionName}
                        placeholder="Max 3 characters"
                        maxLength={3}
                        onChange={(e) => updateField("sectionName", e.target.value)}
                        error={submitted && !form.sectionName}
                        errorMessage="Section name is required"
                    />
                </div>

                {/* Dates */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ flex: 1 }}>
                        <div className="addBatch-label">Start date <span className="required">*</span></div>
                        <DatePicker
                            style={{ width: '100%', height: '42px', borderRadius: '12px' }}
                            value={form.startDate}
                            onChange={handleStartDateChange}
                        />
                        {submitted && !form.startDate && <div style={{ color: '#FF4D4F', fontSize: '12px', marginTop: '6px' }}>Start date is required</div>}
                    </div>
                    <div style={{ flex: 1 }}>
                        <div className="addBatch-label">End date</div>
                        <DatePicker
                            style={{ width: '100%', height: '42px', borderRadius: '12px', opacity: form.startDate ? 1 : 0.5 }}
                            value={form.endDate}
                            onChange={(date) => updateField("endDate", date)}
                            disabledDate={(current) => form.startDate ? current.isBefore(form.startDate, "day") : false}
                            disabled={!form.startDate}
                        />
                    </div>
                </div>

                {/* Times */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ flex: 1 }}>
                        <div className="addBatch-label">Start time <span className="required">*</span></div>
                        <TimePicker
                            format="hh:mm A"
                            style={{ width: '100%', height: '42px', borderRadius: '12px' }}
                            value={form.startTime}
                            onChange={(time) => {
                                updateField("startTime", time);
                                if (
                                    !time ||
                                    (form.endTime &&
                                        moment(form.endTime).format("HH:mm:ss") <= moment(time).format("HH:mm:ss"))
                                ) {
                                    updateField("endTime", null);
                                }
                            }}
                        />
                        {submitted && !form.startTime && <div style={{ color: '#FF4D4F', fontSize: '12px', marginTop: '6px' }}>Start time is required</div>}
                    </div>
                    <div style={{ flex: 1 }}>
                        <div className="addBatch-label">End time <span className="required">*</span></div>
                        <TimePicker
                            format="hh:mm A"
                            style={{ width: '100%', height: '42px', borderRadius: '12px' }}
                            value={form.endTime}
                            disabled={!form.startTime}
                            disabledHours={disabledEndTimeHours}
                            disabledMinutes={disabledEndTimeMinutes}
                            defaultOpenValue={getDefaultEndTime()}
                            onChange={(time) => updateField("endTime", time)}
                        />
                        {submitted && !form.endTime && <div style={{ color: '#FF4D4F', fontSize: '12px', marginTop: '6px' }}>End time is required</div>}
                        {form.startTime && form.endTime && isEndTimeInvalid && (
                            <div style={{ color: '#FF4D4F', fontSize: '12px', marginTop: '6px' }}>
                                End time must be after start time
                            </div>
                        )}
                    </div>
                </div>

                {/* Repeat */}
                <div className="addBatch-field">
                    <div className="addBatch-label">Class(es) repeat on <span className="required">*</span></div>
                    <Dropdown
                        trigger={["click"]}
                        open={repeatDropdownOpen}
                        onOpenChange={setRepeatDropdownOpen}
                        placement="bottomLeft"
                        overlayStyle={{ zIndex: 9999 }}
                        dropdownRender={() => (
                            <div style={{ background: '#fff', borderRadius: '12px', padding: '12px', boxShadow: '0 4px 14px rgba(0,0,0,0.12)' }}>
                                {repeatOptions.map((opt) => (
                                    <div
                                        key={opt}
                                        style={{
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            borderRadius: '8px',
                                            background: form.repeat === opt ? '#D6C8F5' : 'transparent',
                                            color: form.repeat === opt ? '#191919' : '#636363'
                                        }}
                                        onClick={() => {
                                            updateField("repeat", opt);
                                            setRepeatDropdownOpen(false);
                                        }}
                                        onMouseEnter={(e) => { if (form.repeat !== opt) e.target.style.background = '#F6F4FE'; }}
                                        onMouseLeave={(e) => { if (form.repeat !== opt) e.target.style.background = 'transparent'; }}
                                    >
                                        {opt}
                                    </div>
                                ))}
                            </div>
                        )}
                    >
                        <div style={{
                            width: '100%', height: '42px', border: '1px solid #E6E6E6', borderRadius: '12px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px', cursor: 'pointer'
                        }}>
                            <span style={{ color: form.repeat ? '#191919' : '#AEAEAE', fontSize: '14px' }}>
                                {form.repeat || "Select repeat schedule"}
                            </span>
                            <span style={{ color: '#999', transform: repeatDropdownOpen ? 'rotate(90deg)' : 'none', transition: '0.2s' }}>›</span>
                        </div>
                    </Dropdown>
                    {submitted && !form.repeat && <div style={{ color: '#FF4D4F', fontSize: '12px', marginTop: '6px' }}>Repeat schedule is required</div>}
                </div>

                {/* Custom Days */}
                {form.repeat === "Custom" && (
                    <div className="addBatch-field">
                        <div className="addBatch-label">Select the days <span className="required">*</span></div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {daysList.map(({ label, value }) => {
                                const isSelected = form.selectedDays.has(value);
                                return (
                                    <button
                                        key={value}
                                        onClick={() => toggleDay(value)}
                                        style={{
                                            width: '35px', height: '35px', borderRadius: '50%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            border: 'none', cursor: 'pointer', fontWeight: 'bold',
                                            background: isSelected ? '#F3F3FF' : '#E6E6E6',
                                            color: isSelected ? '#594099' : '#AEAEAE'
                                        }}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                        {submitted && form.selectedDays.size === 0 && <div style={{ color: '#FF4D4F', fontSize: '12px', marginTop: '6px' }}>Please select at least one day</div>}
                    </div>
                )}

                {/* Stop Admissions Radio */}
                <div className="addBatch-field">
                    <div className="addBatch-label" style={{ marginBottom: '12px' }}>
                        If maximum student strength in this batch reached?
                    </div>
                    <div style={{ display: 'flex', gap: '32px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                style={{ width: '18px', height: '18px', marginRight: '10px', accentColor: '#594099' }}
                                checked={!form.stopAdmission}
                                onChange={() => {
                                    updateField("stopAdmission", false);
                                    updateField("strength", ""); // Clear strength if not stopping
                                }}
                            />
                            <span style={{ color: '#636363', fontSize: '14px', fontWeight: 400 }}>Do not stop admissions</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                style={{ width: '18px', height: '18px', marginRight: '10px', accentColor: '#594099' }}
                                checked={form.stopAdmission}
                                onChange={() => updateField("stopAdmission", true)}
                            />
                            <span style={{ color: '#636363', fontSize: '14px', fontWeight: 400 }}>Stop admissions</span>
                        </label>
                    </div>
                </div>

                {/* Strength */}
                {form.stopAdmission && (
                    <div className="addBatch-field">
                        <FloatingLabelInput
                            label="Maximum student strength"
                            required
                            value={form.strength}
                            maxLength={4}
                            onChange={(e) => updateField("strength", e.target.value.replace(/\D/g, ""))}
                            error={submitted && !form.strength}
                            errorMessage="Student strength is required"
                        />
                    </div>
                )}

            </div>

            {/* Footer */}
            <div className="add-batch-footer">
                <div className="clear-btn" onClick={() => setForm(defaultForm)}>
                    CLEAR
                </div>
                <Button
                    type="primary"
                    disabled={isCreateDisabled}
                    onClick={handleAdd}
                >
                    ADD
                </Button>
            </div>
        </Drawer>
    );
};

export default AddBatchDrawer;
